import React from 'react';
import { Grid, Typography, TextField, MenuItem, Autocomplete, Box } from '@mui/material';
import { Client } from '../../../../types/client';
import { Property, PropertyAddress } from '../../../../types/property.types';

interface ClientPropertySectionProps {
  clients: Client[];
  properties: Property[];
  selectedClientId?: string;
  selectedPropertyId?: string;
  onClientChange: (clientId: string) => void;
  onPropertyChange: (propertyId: string) => void;
  clientIdFromQuery?: string;
  propertyIdFromQuery?: string;
  isViewMode: boolean;
}

// Format address for display
const formatAddress = (address: PropertyAddress) => {
  if (!address) return 'N/A';
  
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.county,
    address.postcode,
  ].filter(Boolean);
  
  return parts.join(', ');
};

export const ClientPropertySection: React.FC<ClientPropertySectionProps> = ({
  clients,
  properties,
  selectedClientId,
  selectedPropertyId,
  onClientChange,
  onPropertyChange,
  clientIdFromQuery,
  propertyIdFromQuery,
  isViewMode,
}) => {
  return (
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>Client Information</Typography>
      <Grid container spacing={3}>
        {/* Client Selection */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            options={[...clients].sort((a, b) => a.companyName.localeCompare(b.companyName))}
            getOptionLabel={(option) => option.companyName}
            value={clients.find(c => c._id === selectedClientId) || null}
            onChange={(_, newValue) => onClientChange(newValue?._id || '')}
            disabled={Boolean(clientIdFromQuery) || Boolean(isViewMode)}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: 'rgba(30, 30, 30, 0.95)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '& .MuiAutocomplete-listbox': {
                    '& .MuiAutocomplete-option': {
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: 'rgba(25, 118, 210, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.3)',
                        },
                      },
                    },
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                required
                placeholder="Search clients..."
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box>
                  <Typography variant="body1">{option.companyName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.ownerName} • {option.email}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option._id === value._id}
          />
        </Grid>
        
        {/* Property Selection */}
        {selectedClientId && (
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Property"
              value={selectedPropertyId || ''}
              onChange={(e) => onPropertyChange(e.target.value)}
              disabled={Boolean(propertyIdFromQuery) || Boolean(isViewMode)}
              required
            >
              {propertyIdFromQuery && properties.length > 0 ? (
                <MenuItem value={propertyIdFromQuery}>
                  {properties.find(p => p._id === propertyIdFromQuery)?.name || 'Loading property...'}
                </MenuItem>
              ) : (
                properties.map((property) => (
                  <MenuItem key={property._id} value={property._id}>
                    {property.name} - {formatAddress(property.address)}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
